import { useMemo } from "react";
import { InfoCircleOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import { Modal, Popover, Space, Table, Tooltip } from "antd";
import { camelToWords, formatCurrency, formatDate, snakeToCamelCase } from "common/utils";
import { useUserType } from "features/auth";
import { useFetchAccountBalanceHistoryQuery } from "features/debtorAccountsTable/agencyPortal/debtorAccountsAPI";
import { useFetchCreditorPortalAccountBalanceHistoryQuery } from "features/debtorAccountsTable/creditorPortal/debtorAccountsAPI";
import { useFetchUserSummariesQuery } from "features/userManagementTable/userManagementAPI";
import styled from "styled-components";
import { TruncatedText } from "components/truncatedText";
import currency from "currency.js";

const StyledPositiveSpan = styled.span`
  color: green;
`;

const StyledNegativeSpan = styled.span`
  color: red;
`;

const StyledQuestionCircleOutlined = styled(QuestionCircleOutlined)`
  color: #1677ff;
  margin-left: 8px;
  cursor: pointer;
`;

const StyledInfoCircleOutlined = styled(InfoCircleOutlined)`
  color: #1677ff;
  margin-left: 8px;
  cursor: pointer;

  &:hover {
    color: #0056b3;
  }
`;

const StyledAdjustmentNoteDiv = styled.div`
  margin-top: 12px;
`;

const FEE_FIELDS = [
  "attorneyFees",
  "checkFees",
  "collectionFees",
  "courtFees",
  "handlingFees",
  "legalFees",
  "lienFees",
  "miscFees",
  "serviceFees",
  "clientFees",
  "garnishmentFees",
  "postjudgmentAttorneyFees",
];

function renderBalanceHistorySummary({ data, combineInterest }) {
  const lastRow = data?.length ? data[data.length - 1] : null;
  if (!lastRow) return null;
  return (
    <Table.Summary fixed>
      {lastRow.totalPaid &&
        renderBalanceHistorySummaryRow({
          rowKey: "totalPaid",
          label: "Total Paid",
          amounts: lastRow.totalPaid,
          combineInterest,
        })}
      {renderBalanceHistorySummaryRow({
        rowKey: "totalDue",
        label: "Total Due",
        amounts: lastRow.balances,
        combineInterest,
      })}
    </Table.Summary>
  );
}

function renderBalanceHistorySummaryRow({ rowKey, label, amounts, combineInterest }) {
  const interestCells = combineInterest
    ? [
        <Table.Summary.Cell key="interest" index={5}>
          <strong>
            {formatCurrency(
              Number(amounts.interest || 0) +
                Number(amounts.clientInterest || 0) +
                Number(amounts.prejudgmentInterest || 0),
            )}
          </strong>
        </Table.Summary.Cell>,
      ]
    : [
        <Table.Summary.Cell key="agencyInterest" index={5}>
          <strong>{formatCurrency(amounts.interest)}</strong>
        </Table.Summary.Cell>,
        <Table.Summary.Cell key="clientInterest" index={6}>
          <strong>{formatCurrency(amounts.clientInterest)}</strong>
        </Table.Summary.Cell>,
        <Table.Summary.Cell key="prejudgmentInterest" index={7}>
          <strong>{formatCurrency(amounts.prejudgmentInterest)}</strong>
        </Table.Summary.Cell>,
      ];

  const feeStartIndex = combineInterest ? 6 : 8;
  const feeCells = FEE_FIELDS.map((field, i) => (
    <Table.Summary.Cell key={field} index={feeStartIndex + i}>
      {formatCurrency(amounts[field])}
    </Table.Summary.Cell>
  ));
  const totalFeesIndex = feeStartIndex + FEE_FIELDS.length;
  const overpaymentsIndex = totalFeesIndex + 1;
  const totalIndex = overpaymentsIndex + 1;

  return (
    <Table.Summary.Row key={rowKey}>
      <Table.Summary.Cell index={0} colSpan={4}>
        <strong>{label}</strong>
      </Table.Summary.Cell>
      <Table.Summary.Cell index={4}>
        <strong>{formatCurrency(amounts.principal)}</strong>
      </Table.Summary.Cell>
      {interestCells}
      {feeCells}
      <Table.Summary.Cell index={totalFeesIndex}>
        <strong>{formatCurrency(amounts.totalFees)}</strong>
      </Table.Summary.Cell>
      <Table.Summary.Cell index={overpaymentsIndex}> </Table.Summary.Cell>
      <Table.Summary.Cell index={totalIndex}>
        <strong>{formatCurrency(amounts.total)}</strong>
      </Table.Summary.Cell>
    </Table.Summary.Row>
  );
}

function BalanceHistoryModal({ account, open, onCancel, combineInterest }) {
  const { isAgencyUserType } = useUserType();
  const { data: users } = useFetchUserSummariesQuery({}, { skip: !isAgencyUserType });
  const userIdToName = useMemo(() => {
    const map = {};
    (users ?? []).forEach((user) => {
      map[user.id] = user.name;
    });
    return map;
  }, [users]);
  const { data: agencyAccountBalanceHistoryData } = useFetchAccountBalanceHistoryQuery(
    { accountId: account?.id },
    {
      skip: !account?.id || !isAgencyUserType,
    },
  );
  const { data: creditorPortalAccountBalanceHistoryData } =
    useFetchCreditorPortalAccountBalanceHistoryQuery(
      { accountId: account?.id },
      {
        skip: !account?.id || isAgencyUserType,
      },
    );
  const data = isAgencyUserType
    ? agencyAccountBalanceHistoryData
    : creditorPortalAccountBalanceHistoryData;
  const columns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (text, record) => formatDate(record.date),
    },
    {
      title: "Action Taken",
      dataIndex: "type",
      key: "type",
      render: (text, record) => {
        const createdByName = record.createdById && userIdToName[record.createdById];
        const showPopover = record.notes || createdByName;

        if (!showPopover) {
          return <span>{camelToWords(snakeToCamelCase(record.type))}</span>;
        }

        return (
          <span>
            {camelToWords(snakeToCamelCase(record.type))}
            <Popover
              content={
                <>
                  {createdByName && (
                    <div>
                      <strong>Created By</strong>
                      <div>{createdByName}</div>
                    </div>
                  )}
                  {record.notes && (
                    <StyledAdjustmentNoteDiv>
                      <strong>Adjustment Note</strong>
                      <div>
                        <TruncatedText text={record.notes} maxLength={40} />
                      </div>
                    </StyledAdjustmentNoteDiv>
                  )}
                </>
              }
              trigger="click"
            >
              <StyledInfoCircleOutlined />
            </Popover>
          </span>
        );
      },
    },
    {
      title: "Total Amount Changed",
      dataIndex: "total",
      render: (text, record) => {
        if (parseFloat(record.deltas.total) > 0) {
          return <StyledPositiveSpan>+{formatCurrency(record.deltas.total)}</StyledPositiveSpan>;
        }
        if (parseFloat(record.deltas.total) < 0) {
          return <StyledNegativeSpan>{formatCurrency(record.deltas.total)}</StyledNegativeSpan>;
        }
        return formatCurrency(record.deltas.total);
      },
    },
    {
      title: "Daily Interest Accrual",
      key: "dailyInterestAccrual",
      render: (text, record) => {
        const dailyInterestAccrual = currency(record.balances.principal)
          .multiply(account?.interestRate)
          .divide(365).value;
        return (
          <>
            <span>{formatCurrency(dailyInterestAccrual)}</span>
            <Tooltip
              title={`You are accruing ${formatCurrency(
                dailyInterestAccrual,
              )} daily on this account`}
              trigger="hover"
            >
              <StyledQuestionCircleOutlined />
            </Tooltip>
          </>
        );
      },
    },
    {
      title: "Balance",
      children: [
        {
          title: "Principal",
          dataIndex: "principal",
          render: (text, record) => <strong>{formatCurrency(record.balances.principal)}</strong>,
        },
        ...(() => {
          // We combine the interest on the creditor portal, so they don't see the breakdown
          if (combineInterest) {
            return [
              {
                title: "Interest",
                dataIndex: "totalInterest",
                render: (text, record) => {
                  const agencyInterest = Number(record.balances.interest || 0);
                  const clientInterest = Number(record.balances.clientInterest || 0);
                  const prejudgmentInterest = Number(record.balances.prejudgmentInterest || 0);
                  return (
                    <strong>
                      {formatCurrency(agencyInterest + clientInterest + prejudgmentInterest)}
                    </strong>
                  );
                },
              },
            ];
          }

          return [
            {
              title: "Agency Interest",
              dataIndex: "interest",
              render: (text, record) => <strong>{formatCurrency(record.balances.interest)}</strong>,
            },
            {
              title: "Client Interest",
              dataIndex: "clientInterest",
              render: (text, record) => (
                <strong>{formatCurrency(record.balances.clientInterest)}</strong>
              ),
            },
            {
              title: "Prejudgment Interest",
              dataIndex: "prejudgmentInterest",
              render: (text, record) => (
                <strong>{formatCurrency(record.balances.prejudgmentInterest)}</strong>
              ),
            },
          ];
        })(),
        {
          title: "Fees",
          children: [
            {
              title: "Attorney Fees",
              dataIndex: "attorneyFees",
              render: (text, record) => formatCurrency(record.balances.attorneyFees),
            },
            {
              title: "Check Fees",
              dataIndex: "checkFees",
              render: (text, record) => formatCurrency(record.balances.checkFees),
            },
            {
              title: "Collection Fees",
              dataIndex: "collectionFees",
              render: (text, record) => formatCurrency(record.balances.collectionFees),
            },
            {
              title: "Court Fees",
              dataIndex: "courtFees",
              render: (text, record) => formatCurrency(record.balances.courtFees),
            },
            {
              title: "Handling Fees",
              dataIndex: "handlingFees",
              render: (text, record) => formatCurrency(record.balances.handlingFees),
            },
            {
              title: "Legal Fees",
              dataIndex: "legalFees",
              render: (text, record) => formatCurrency(record.balances.legalFees),
            },
            {
              title: "Lien Fees",
              dataIndex: "lienFees",
              render: (text, record) => formatCurrency(record.balances.lienFees),
            },
            {
              title: "Misc Fees",
              dataIndex: "miscFees",
              render: (text, record) => formatCurrency(record.balances.miscFees),
            },
            {
              title: "Service Fees",
              dataIndex: "serviceFees",
              render: (text, record) => formatCurrency(record.balances.serviceFees),
            },
            {
              title: "Client Fees",
              dataIndex: "clientFees",
              render: (text, record) => formatCurrency(record.balances.clientFees),
            },
            {
              title: "Garnishment Fees",
              dataIndex: "garnishmentFees",
              render: (text, record) => formatCurrency(record.balances.garnishmentFees),
            },
            {
              title: "Postjudgment Attorney Fees",
              dataIndex: "postjudgmentAttorneyFees",
              render: (text, record) => formatCurrency(record.balances.postjudgmentAttorneyFees),
            },
            {
              title: "Total Fees",
              dataIndex: "totalFees",
              render: (text, record) => (
                <strong>{formatCurrency(record.balances.totalFees)}</strong>
              ),
            },
          ],
        },
        {
          title: "Overpayments",
          dataIndex: "overpayments",
          render: (text, record) => (
            <Space>
              {formatCurrency(record.overpayment)}
              {record.originalOverpayment !== record.overpayment && (
                <Tooltip
                  title={`Original Amount: ${formatCurrency(record.originalOverpayment)}`}
                  trigger="hover"
                >
                  <InfoCircleOutlined />
                </Tooltip>
              )}
            </Space>
          ),
        },
        // {
        //   title: "Prior Interest",
        //   dataIndex: "priorInterest",
        //   render: (text, record) => formatCurrency(record.balances.priorInterest),
        // },
        // {
        //   title: "Treble Damages",
        //   dataIndex: "trebleDamages",
        //   render: (text, record) => formatCurrency(record.balances.trebleDamages),
        // },
        {
          title: "Total",
          dataIndex: "total",
          render: (text, record) => <strong>{formatCurrency(record.balances.total)}</strong>,
        },
      ],
    },
  ];

  return (
    <Modal
      width={1100}
      maskClosable={false}
      title="Balance History"
      open={open}
      onOk={onCancel}
      onCancel={onCancel}
      footer={null}
    >
      <Table
        bordered
        scroll={{ x: "max-content" }}
        // @ts-ignore
        columns={columns}
        dataSource={data}
        summary={() => renderBalanceHistorySummary({ data, combineInterest })}
      />
    </Modal>
  );
}

export default BalanceHistoryModal;
